import React, { useEffect, useMemo, useState } from "react";
import Header from "../../components/layout/Header";
import { useAuth } from "../../context/AuthContext";
import { getAvailableJobs, applyToJob } from "../../services/jobAPI";
import {
  FiBriefcase,
  FiMapPin,
  FiClock,
  FiBookmark,
  FiSearch,
  FiChevronDown,
} from "react-icons/fi";

import ApplyModal from "./ApplyModal";

// New details (page-style) component (create this file)
import JobDetailsPage from "./JobDetailsPage";

// ADD THIS: MyApplications component
import MyApplications from "../../pages/JobApplication/CandidateApplication/MyApplications";

// Keep existing JobFeed.css if you still use parts of it
import "./styles/JobFeed.css";

// New UI css (create this file from previous message)
import "./styles/NewJobUI.css";

const JobFeed = () => {
  const { token } = useAuth();

  // Tabs (UI for now)
  const [activeTab, setActiveTab] = useState("JOB_BOARD"); // JOB_BOARD | SAVED | IN_PROGRESS | ARCHIVED

  // Job list state
  const [jobs, setJobs] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [filters, setFilters] = useState({
    department: "",
    type: "",
    locationType: "",
    search: "",
  });

  // View state (list vs details)
  const [view, setView] = useState("LIST"); // LIST | DETAILS
  const [selectedJob, setSelectedJob] = useState(null);

  // Apply modal
  const [showApply, setShowApply] = useState(false);
  const [applySuccess, setApplySuccess] = useState("");

  useEffect(() => {
    async function fetchJobs() {
      setLoading(true);
      try {
        const res = await getAvailableJobs();
        setAllJobs(res.data || []);
        setJobs(res.data || []);
        setError("");
      } catch (e) {
        setError("Failed to load jobs");
      } finally {
        setLoading(false);
      }
    }
    fetchJobs();
  }, []);

  useEffect(() => {
    let filtered = allJobs;

    if (filters.department) {
      filtered = filtered.filter(
        (job) =>
          job.department &&
          job.department.toLowerCase() === filters.department.toLowerCase()
      );
    }

    if (filters.type) {
      filtered = filtered.filter(
        (job) => job.type && job.type.toLowerCase() === filters.type.toLowerCase()
      );
    }

    if (filters.locationType) {
      filtered = filtered.filter(
        (job) =>
          job.locationType &&
          job.locationType.toLowerCase() === filters.locationType.toLowerCase()
      );
    }

    if (filters.search) {
      const q = filters.search.toLowerCase();
      filtered = filtered.filter(
        (job) =>
          job.title?.toLowerCase().includes(q) ||
          job.description?.toLowerCase().includes(q)
      );
    }

    setJobs(filtered);
  }, [filters, allJobs]);

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const departments = useMemo(() => {
    const set = new Set((allJobs || []).map((j) => j.department).filter(Boolean));
    return ["", ...Array.from(set)];
  }, [allJobs]);

  const openJobDetails = (job) => {
    setSelectedJob(job);
    setView("DETAILS");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBackToBoard = () => {
    setView("LIST");
    setSelectedJob(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeApplyModal = () => setShowApply(false);

  const handleApplicationSubmit = async (formData) => {
    try {
      await applyToJob(selectedJob.id, formData, token);
      setApplySuccess("Application submitted successfully.");
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  useEffect(() => {
    if (!applySuccess) return;
    const timer = setTimeout(() => setApplySuccess(""), 2000);
    return () => clearTimeout(timer);
  }, [applySuccess]);

  return (
    <>
      <Header />

      <div className="jb-page">
        {/* Top pill tabs (new UI) */}
        <div className="jb-tabs">
          <button
            className={`jb-tab ${activeTab === "JOB_BOARD" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("JOB_BOARD");
              setView("LIST");
            }}
          >
            Job Board
          </button>

          <button
            className={`jb-tab ${activeTab === "SAVED" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("SAVED");
              setView("LIST");
            }}
          >
            Saved
          </button>

          <button
            className={`jb-tab ${activeTab === "IN_PROGRESS" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("IN_PROGRESS");
              setView("LIST");
            }}
          >
            In Progress
          </button>

          <button
            className={`jb-tab ${activeTab === "ARCHIVED" ? "active" : ""}`}
            onClick={() => {
              setActiveTab("ARCHIVED");
              setView("LIST");
            }}
          >
            Archived
          </button>
        </div>

        {/* Success banner */}
        {applySuccess && (
          <div className="success-banner-backdrop">
            <div className="success-banner">{applySuccess}</div>
          </div>
        )}

        {/* JOB BOARD */}
        {activeTab === "JOB_BOARD" && (
          <>
            {view === "DETAILS" ? (
              <JobDetailsPage
                job={selectedJob}
                onBack={goBackToBoard}
                onApply={() => setShowApply(true)}
                onSave={() => {
                  // UI-only for now (no backend attached for saved jobs)
                }}
              />
            ) : (
              <div className="jb-board">
                <h2>Job Board</h2>
                <p className="jb-subtitle">
                  Browse active job postings and find your next opportunity
                </p>

                {/* Filters row (new UI layout) */}
                <div className="jb-filters">
                  <div className="jb-filter">
                    <label>Department</label>
                    <div className="jb-select">
                      <select
                        name="department"
                        value={filters.department}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Departments</option>
                        {departments
                          .filter((d) => d !== "")
                          .map((d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                      </select>
                      <FiChevronDown className="jb-select-icon" />
                    </div>
                  </div>

                  <div className="jb-filter">
                    <label>Location</label>
                    <div className="jb-select">
                      <select
                        name="locationType"
                        value={filters.locationType}
                        onChange={handleFilterChange}
                      >
                        <option value="">All Locations</option>
                        <option value="ONSITE">Onsite</option>
                        <option value="REMOTE">Remote</option>
                        <option value="HYBRID">Hybrid</option>
                      </select>
                      <FiChevronDown className="jb-select-icon" />
                    </div>
                  </div>

                  <div className="jb-filter">
                    <label>Employment Type</label>
                    <div className="jb-select">
                      <select name="type" value={filters.type} onChange={handleFilterChange}>
                        <option value="">All Types</option>
                        <option value="FULL_TIME">Full-time</option>
                        <option value="PART_TIME">Part-time</option>
                        <option value="CONTRACT">Contract</option>
                        <option value="INTERNSHIP">Internship</option>
                      </select>
                      <FiChevronDown className="jb-select-icon" />
                    </div>
                  </div>
                </div>

                <div className="jb-search">
                  <FiSearch className="jb-search-icon" />
                  <input
                    name="search"
                    value={filters.search}
                    onChange={handleFilterChange}
                    placeholder="Search jobs..."
                  />
                </div>

                {error && <div className="error">{error}</div>}

                {loading ? (
                  <div style={{ color: "#6b7280", padding: 10 }}>Loading jobs...</div>
                ) : jobs.length === 0 ? (
                  <div style={{ color: "#6b7280", padding: 10 }}>No jobs found</div>
                ) : (
                  <div className="jb-list">
                    {jobs.map((job) => (
                      <div key={job.id} className="jb-jobcard">
                        <div className="jb-jobcard-left">
                          <h3 className="jb-jobcard-title">{job.title}</h3>

                          <div className="jb-jobcard-meta">
                            <span className="jb-meta-item">
                              <FiBriefcase className="jb-meta-icon" />
                              {job.department || "—"}
                            </span>

                            <span className="jb-meta-item">
                              <FiMapPin className="jb-meta-icon" />
                              {job.location || "—"}
                            </span>

                            <span className="jb-meta-item">
                              <FiClock className="jb-meta-icon" />
                              {job.type || "—"}
                            </span>
                          </div>

                          <p className="jb-jobcard-desc">{job.description || "—"}</p>

                          <div className="jb-jobcard-posted">
                            <FiClock className="jb-meta-icon" />
                            Posted:{" "}
                            {job.publishedAt
                              ? new Date(job.publishedAt).toLocaleDateString()
                              : "—"}
                          </div>
                        </div>

                        <div className="jb-jobcard-right">
                          <div className="jb-right-top">
                            <span className="jb-badge jb-badge--active">Active</span>

                            <button
                              type="button"
                              className="jb-icon-btn"
                              title="Save"
                              onClick={() => {
                                // UI-only for now
                              }}
                            >
                              <FiBookmark />
                            </button>
                          </div>

                          <button
                            className="jb-btn jb-btn--primary"
                            onClick={() => openJobDetails(job)}
                          >
                            View Details
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* SAVED */}
        {activeTab === "SAVED" && (
          <div className="jb-board">
            <h2>Saved Jobs</h2>
            <p className="jb-subtitle">Jobs you've bookmarked for later review</p>

            <div
              style={{
                marginTop: 18,
                border: "1px solid #e5e7eb",
                borderRadius: 16,
                padding: 40,
                textAlign: "center",
                color: "#6b7280",
              }}
            >
              No saved jobs yet
            </div>
          </div>
        )}

        {/* IN PROGRESS (UPDATED: render MyApplications) */}
        {activeTab === "IN_PROGRESS" && (
          <div className="jb-board">
            <h2>My Applications</h2>
            <p className="jb-subtitle">Track the progress of your job applications</p>

            {/* This keeps your existing MyApplications UI/table unchanged */}
            <MyApplications />
          </div>
        )}

        {/* ARCHIVED */}
        {activeTab === "ARCHIVED" && (
          <div className="jb-board">
            <h2>Application Archive</h2>
            <p className="jb-subtitle">
              All your previous job applications with final outcomes
            </p>
            <div style={{ marginTop: 18, color: "#6b7280" }}>
              Archived UI will be wired to getCandidateApplications later.
            </div>
          </div>
        )}

        {/* Apply modal stays as-is */}
        <ApplyModal
          open={showApply}
          job={selectedJob}
          onClose={closeApplyModal}
          onSubmit={handleApplicationSubmit}
        />
      </div>
    </>
  );
};

export default JobFeed;
